/**
 * Confirmation dialog shown when the developer presses save in the schema
 * editor. The migration may drop content fields that no longer fit the new
 * schema, so we surface the full impact with a JSON-pointer-keyed list and
 * require an explicit confirm. When drops are involved, the confirm button
 * is destructive-styled and the user must check a "I understand" box first.
 */

import type { MigrationResult } from '@bliksys/schema-engine';
import { useEffect, useId, useRef, useState } from 'react';

import { MigrationImpact } from '../components/MigrationImpact.js';

import './SchemaSaveDialog.css';

interface Props {
  migration: MigrationResult;
  onConfirm: () => void;
  onCancel: () => void;
}

export function SchemaSaveDialog({ migration, onConfirm, onCancel }: Props): JSX.Element {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const ackId = useId();
  const [acknowledged, setAcknowledged] = useState(false);
  const { summary, changes } = migration;

  // Severity drives whether we require an explicit acknowledgement. Drops
  // are destructive (lost content); defaults are usually fine; an empty
  // change list is a pure no-op.
  const hasDrops = summary.dropped > 0;
  const hasChanges = changes.length > 0;

  useEffect(() => {
    const d = dialogRef.current;
    if (d && !d.open) d.showModal();
  }, []);

  return (
    <dialog ref={dialogRef} className="schema-save-dialog" onClose={onCancel}>
      <header className="schema-save-header">
        <h2 className="schema-save-title">Save schema?</h2>
        <p className="schema-save-subtitle">
          {hasChanges
            ? 'Saving will migrate the existing content to fit the new schema.'
            : 'No content changes — the new schema is structurally compatible.'}
        </p>
      </header>

      <MigrationImpact migration={migration} defaultOpen={hasDrops} />

      {hasDrops && (
        <label className="schema-save-ack" htmlFor={ackId}>
          <input
            id={ackId}
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
          />
          <span>
            I understand that {summary.dropped} field{summary.dropped === 1 ? '' : 's'} of content
            will be permanently dropped.
          </span>
        </label>
      )}

      <footer className="schema-save-footer">
        <button type="button" className="btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className={hasDrops ? 'btn-danger' : 'btn-primary'}
          disabled={hasDrops && !acknowledged}
          onClick={onConfirm}
        >
          {hasDrops ? 'Save and drop content' : 'Save schema'}
        </button>
      </footer>
    </dialog>
  );
}
